import { useCallback, useEffect, useState } from 'react';

export interface AsyncData<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

/**
 * Load async data with proper hygiene the admin app was missing: a `.catch`
 * that surfaces an ERROR state (instead of a silent `.then()`), and an
 * active-flag guard so a resolved/rejected fetch never calls setState after the
 * component unmounts or the loader changes. Pass a STABLE loader (useCallback).
 */
export function useAsyncData<T>(loader: () => Promise<T>): AsyncData<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `nonce` is the reload trigger — bumping it must re-run the effect.
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    loader()
      .then((d) => {
        if (active) {
          setData(d);
          setLoading(false);
        }
      })
      .catch((e: unknown) => {
        if (active) {
          setError(e instanceof Error ? e.message : 'Failed to load');
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [loader, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, loading, reload };
}
